/**
 * This component is the collected Vendor Application.
 */
import { isEmpty, get } from 'lodash';
import React, { useMemo, useState } from 'react';
import cx from 'classnames';

import PropTypes from 'prop-types';
import { Prompt, Redirect } from 'react-router-dom';
import styles from './InfluencerUserInfo.module.css';
import Button from '../ReusableComponents/Button/Button';
import ContactInfoFields from './ContactInfoFields';
import { useMutateUser } from '../../Lib/Hooks/user.hooks';
import UserNamesFields from './UserNamesFields';
import LoadingText from '../ReusableComponents/LoadingText/LoadingText';
import FileInput from '../FileInput/FileInput';
import Page from '../Page/index.ts';
import Header from '../Header/index.ts';
import { Actions, hasPermissionTo } from '../../Lib/Constants/roles.ts';
import EmailUpdateDialog from './EmailUpdateDialog.tsx';

export default function InfluencerUserInfo(props) {
  const updateUserMutation = useMutateUser();
  const [updatedFields, setUpdatedFields] = useState({});
  const [saved, setSaved] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    first_name: props.user.first_name,
    last_name: props.user.last_name,
    vendor_name: props.user.vendor_name,
    email: props.user.email,
    phone: props.user.phone,
  });
  const [isEmailUpdateModalOpen, setIsEmailUpdateModalOpen] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);

  /**
   * Updates the form data in the form, checks the old form data vs the new
   * changes, and updates the vendor record.
   * @param {string} name the name of the input being updated
   * @param {string} value the value of the updated input
   */
  const updateFormField = (name, value) => {
    const updates = { [name]: value };
    setForm((f) => ({ ...f, ...updates }));
    setSaved(false);
    setUpdatedFields((f) => ({ ...f, ...updates }));
  };

  /**
   * Checks that the form is complete and whether it has been submitted or
   * needs to be saved for later completion.
   * @param {object?} e the event passed back when the submit button is clicked
   * on the form.
   */
  const submit = (e) => {
    if (e) e.preventDefault();

    if (Object.keys(updatedFields).length === 0) return false;

    if (updatedFields.email) {
      const isSameEmail =
        updatedFields.email.toLowerCase() === props.user.email.toLowerCase();
      if (!isSameEmail) {
        setIsEmailUpdateModalOpen(true);
        return false;
      }
      return submitUpdate();
    }

    return submitUpdate();
  };

  const submitUpdate = async () =>
    new Promise((resolve, reject) => {
      const successCb = (data) => {
        setSaved(true);
        setUpdatedFields({});
        props.updateUserSuccess(data.user);
        resolve(data);
      };

      const errorCb = (error) => {
        reject(error);
      };

      const mutationArgs = {
        data: {
          id: props.user.id,
          ...updatedFields,
        },
        successCb,
        errorCb,
      };

      updateUserMutation.mutate(mutationArgs);
    });

  const handleFormErrors = (field, error) => {
    const updates = { [field]: error || null };
    setErrors((e) => ({ ...e, ...updates }));
  };

  const { publicFilesUrls, user, currentUser } = props;

  const perms = useMemo(() => {
    const canUpdateForm = hasPermissionTo(
      'users/:id/influencer-user-info',
      currentUser.role,
      Actions.UPDATE,
      'FORM',
    );
    return {
      canUpdateForm,
    };
  }, [currentUser]);

  if (isEmpty(publicFilesUrls)) return null;
  if (!user || isEmpty(user)) return null;

  const influencerManualFileLink = get(
    publicFilesUrls,
    'influencer_vendor_portal_user_manual.read',
  );

  const hasUpdate = Object.keys(updatedFields).length > 0;
  const hasFormErrors = Object.values(errors).includes('error');

  const handleSubmitAndRedirect = async () => {
    if (updatedFields.email === props.user.email) {
      await submitUpdate();
      return;
    }

    if (!isEmailUpdateModalOpen) {
      return;
    }

    await submitUpdate();
    setIsEmailUpdateModalOpen(false);

    if (props.currentUser.role === 'INFLUENCER') {
      localStorage.clear();
      sessionStorage.clear();
      setShouldRedirect(true);
    }
  };

  if (shouldRedirect && props.currentUser.role === 'INFLUENCER') {
    return <Redirect to="/influencer-login" />;
  }

  return (
    <Page.Container>
      <Header.Major icon="briefcase-job" title="Profile" />
      <Page.Content>
        <form className={styles.formContainer} onSubmit={submit}>
          <h2 className={styles.profileHeader}>User Information</h2>
          <br />
          <Prompt
            when={Object.keys(updatedFields).length > 0}
            message="Your changes have not been saved, do you want to quit anyway?"
          />
          <UserNamesFields
            form={form}
            onChange={updateFormField}
            onErrorUpdate={handleFormErrors}
            disabled={!perms.canUpdateForm}
          />
          <br />
          <ContactInfoFields
            form={form}
            onChange={updateFormField}
            onErrorUpdate={handleFormErrors}
            disabled={!perms.canUpdateForm}
          />

          {influencerManualFileLink && (
            <div className={styles.documentContainer}>
              <div className={styles.row}>
                <FileInput
                  name="influencer_vendor_portal_user_manual"
                  header="INFLUENCER VENDOR PORTAL USER MANUAL"
                  suffix="influencer_vendor_portal_user_manual"
                  downloadText="Influencer Vendor Portal User Manual Download"
                  desc="Download influencers user manual."
                  defaultFileLink={influencerManualFileLink}
                  fileLink={influencerManualFileLink}
                  exists={false}
                  fileType={['application/pdf']}
                  fullWidth
                  disabled
                />
              </div>
            </div>
          )}

          <div className={styles.buttonContainer}>
            {updateUserMutation.isError ? (
              <p className={styles.error}>
                An error occured: {updateUserMutation.error}
              </p>
            ) : null}
            {hasFormErrors ? (
              <p className={styles.error}>
                Please make sure all fields are valid
              </p>
            ) : null}
            <Button
              type="submit"
              className={cx({
                [styles.saveButton]: true,
                [styles.saveButtonDisabled]: hasUpdate === false,
              })}
              variant="contained"
              onClick={handleSubmitAndRedirect}
              color="primary"
              disabled={
                (hasUpdate === false && hasFormErrors === true) ||
                !perms.canUpdateForm
              }
            >
              {updateUserMutation.isLoading ? (
                <LoadingText text="Searching" />
              ) : null}
              {saved ? 'Saved!' : 'Save'}
            </Button>
          </div>
        </form>
      </Page.Content>
      <EmailUpdateDialog
        isOpen={isEmailUpdateModalOpen}
        oldEmail={props.user.email}
        newEmail={updatedFields.email}
        onClose={() => setIsEmailUpdateModalOpen(false)}
        onConfirm={handleSubmitAndRedirect}
        userRole={props.currentUser.role}
      />
    </Page.Container>
  );
}

InfluencerUserInfo.propTypes = {
  updateUserSuccess: PropTypes.func.isRequired,
  currentUser: PropTypes.shape({
    role: PropTypes.string,
  }).isRequired,
  user: PropTypes.shape({
    id: PropTypes.number,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    vendor_name: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }).isRequired,
  publicFilesUrls: PropTypes.shape({}).isRequired,
};
